import { useEffect, useState } from 'react';
import { Loader2, Upload, Search, Cpu, AlertTriangle, CheckCircle2, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

const API = process.env.REACT_APP_BACKEND_URL;

const SUB_TABS = [
  { id: 'recognition', label: 'Recognition Events' },
  { id: 'knowledge', label: 'Knowledge Base' },
  { id: 'research_queue', label: 'Research Queue', placeholder: true },
  { id: 'data_requests', label: 'Data Request Queue', placeholder: true },
  { id: 'drivers', label: 'Driver Registry', placeholder: true },
];

const CATEGORIES = [
  'p1_meter', 'smart_meter', 'kwh_meter', 'inverter', 'ev_charger',
  'heat_pump', 'airco', 'boiler', 'battery', 'smart_plug',
  'energy_socket', 'home_assistant_bridge', 'home_automation_hub',
  'gas_meter_via_p1', 'unknown',
];

const STATUS_BADGE = {
  proposed: 'bg-slate-100 text-slate-700',
  pending_review: 'bg-amber-100 text-amber-800',
  approved_read_only: 'bg-emerald-100 text-emerald-800',
  approved_limited_control: 'bg-blue-100 text-blue-800',
  approved_control: 'bg-purple-100 text-purple-800',
  unsupported: 'bg-rose-100 text-rose-800',
  needs_more_info: 'bg-orange-100 text-orange-800',
};


function StatusBanner({ status }) {
  if (!status) return null;
  return (
    <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4" data-testid="dia-status-banner">
      <div className="flex flex-wrap items-center gap-3 text-sm">
        <span className="font-semibold text-emerald-900">DIA</span>
        <span className="text-emerald-800">
          feature_enabled=<b>{String(status.feature_enabled)}</b>
        </span>
        <span className="flex items-center gap-1 text-emerald-800">
          <Lock className="w-3.5 h-3.5" /> control_claims_lock=<b>{String(status.control_claims_lock)}</b>
        </span>
        <span className="text-emerald-800">
          recognition_events=<b>{status.counts?.recognition_events ?? 0}</b>
        </span>
        <span className="text-emerald-800">
          knowledge_base=<b>{status.counts?.knowledge_base ?? 0}</b>
        </span>
        <span className="text-emerald-800">
          driver_registry=<b>{status.counts?.driver_registry ?? 0}</b>
        </span>
      </div>
    </div>
  );
}


function RecognitionEventsTab({ token, onLinkKnowledge }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadMode, setUploadMode] = useState('manual'); // manual | photo | qr
  const [form, setForm] = useState({ brand: '', model: '', category: 'p1_meter', qr: '', photo: null });
  const [busy, setBusy] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetch(`${API}/api/admin/dia/recognition/events?limit=100`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(d => { if (!cancelled) setEvents(d.items || []); })
      .catch(() => { if (!cancelled) setEvents([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [token, reloadKey]);

  const reload = () => setReloadKey(k => k + 1);

  const submit = async () => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('source', uploadMode);
      if (uploadMode === 'manual') {
        fd.append('manual_brand', form.brand);
        fd.append('manual_model', form.model);
        fd.append('manual_category', form.category);
      } else if (uploadMode === 'qr') {
        fd.append('qr_or_barcode_payload', form.qr);
      } else if (uploadMode === 'photo') {
        if (!form.photo) { setBusy(false); return; }
        fd.append('photo', form.photo);
      }
      const r = await fetch(`${API}/api/admin/dia/recognition/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      await r.json();
      setForm({ brand: '', model: '', category: 'p1_meter', qr: '', photo: null });
      reload();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="dia-recognition-tab">
      <div className="rounded-lg border bg-card p-4 space-y-3">
        <h3 className="text-sm font-semibold flex items-center gap-2">
          <Upload className="w-4 h-4" /> New recognition event
        </h3>
        <div className="flex gap-2 flex-wrap">
          {['manual', 'photo', 'qr'].map(m => (
            <Button
              key={m}
              size="sm"
              variant={uploadMode === m ? 'default' : 'outline'}
              onClick={() => setUploadMode(m)}
              data-testid={`dia-upload-mode-${m}`}
            >
              {m}
            </Button>
          ))}
        </div>

        {uploadMode === 'manual' && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <Input placeholder="Brand" value={form.brand} onChange={e => setForm({ ...form, brand: e.target.value })} data-testid="dia-manual-brand" />
            <Input placeholder="Model" value={form.model} onChange={e => setForm({ ...form, model: e.target.value })} data-testid="dia-manual-model" />
            <select
              className="border rounded-md px-3 py-2 text-sm bg-background"
              value={form.category}
              onChange={e => setForm({ ...form, category: e.target.value })}
              data-testid="dia-manual-category"
            >
              {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        )}

        {uploadMode === 'qr' && (
          <Input placeholder="QR / barcode payload" value={form.qr} onChange={e => setForm({ ...form, qr: e.target.value })} data-testid="dia-qr-payload" />
        )}

        {uploadMode === 'photo' && (
          <div className="space-y-2">
            <Input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={e => setForm({ ...form, photo: e.target.files?.[0] || null })}
              data-testid="dia-photo-input"
            />
            <p className="text-xs text-muted-foreground">JPEG/PNG/WEBP only. Admin-only. Photos retained 90 days then purged.</p>
          </div>
        )}

        <Button
          onClick={submit}
          disabled={busy}
          data-testid="dia-recognition-submit"
        >
          {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
          Create recognition event
        </Button>
      </div>

      <div className="rounded-lg border bg-card">
        <div className="px-4 py-3 border-b text-sm font-semibold">
          Recent events ({events.length})
        </div>
        {loading ? (
          <div className="p-6 text-center text-sm text-muted-foreground">Loading...</div>
        ) : events.length === 0 ? (
          <div className="p-6 text-center text-sm text-muted-foreground" data-testid="dia-recognition-empty">No events yet.</div>
        ) : (
          <div className="divide-y" data-testid="dia-recognition-list">
            {events.map(ev => (
              <div key={ev.id} className="p-4 text-sm space-y-1" data-testid={`dia-recognition-row-${ev.id}`}>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-mono text-xs text-muted-foreground">{ev.id}</span>
                  <span className="px-2 py-0.5 text-xs rounded bg-slate-100">{ev.source}</span>
                  <span className={`px-2 py-0.5 text-xs rounded ${ev.extraction_status === 'extracted' ? 'bg-emerald-100 text-emerald-800' : ev.extraction_status === 'manual_required' ? 'bg-amber-100 text-amber-800' : 'bg-slate-100'}`}>
                    {ev.extraction_status}
                  </span>
                  {ev.linked_knowledge_id && (
                    <span className="px-2 py-0.5 text-xs rounded bg-blue-100 text-blue-800">linked</span>
                  )}
                  <span className="text-xs text-muted-foreground">{ev.created_at?.slice(0, 19)}</span>
                </div>
                {ev.extracted && (
                  <div className="text-xs text-muted-foreground">
                    <b>{ev.extracted.brand || '?'}</b> / {ev.extracted.model || '?'}
                    {ev.extracted.category && <> · {ev.extracted.category}</>}
                    {ev.extracted.confidence_band && <> · conf={ev.extracted.confidence_band}</>}
                  </div>
                )}
                {ev.extraction_error && (
                  <div className="text-xs text-rose-600 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" /> {ev.extraction_error.slice(0, 120)}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


function KnowledgeBaseTab({ token }) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState('');
  const [transitionBusy, setTransitionBusy] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const url = `${API}/api/admin/dia/knowledge${q ? `?q=${encodeURIComponent(q)}` : ''}`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => { if (!cancelled) setRecords(d.items || []); })
      .catch(() => { if (!cancelled) setRecords([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [token, q, reloadKey]);

  const reload = () => setReloadKey(k => k + 1);

  const doTransition = async (kid, target) => {
    setTransitionBusy(`${kid}:${target}`);
    try {
      const r = await fetch(`${API}/api/admin/dia/knowledge/${kid}/transition`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ target_status: target, reason: 'Admin click via DIA UI' }),
      });
      const d = await r.json();
      if (!d.ok) {
        alert(`Blocked: ${d.blocked}`);
      }
      reload();
    } finally {
      setTransitionBusy(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="dia-knowledge-tab">
      <div className="flex gap-2 items-center">
        <Search className="w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Search brand / model / slug"
          value={q}
          onChange={e => setQ(e.target.value)}
          className="max-w-xs"
          data-testid="dia-kb-search"
        />
      </div>

      <div className="rounded-lg border bg-card">
        <div className="px-4 py-3 border-b text-sm font-semibold">
          Knowledge records ({records.length})
        </div>
        {loading ? (
          <div className="p-6 text-center text-sm text-muted-foreground">Loading...</div>
        ) : records.length === 0 ? (
          <div className="p-6 text-center text-sm text-muted-foreground" data-testid="dia-kb-empty">No knowledge records yet.</div>
        ) : (
          <div className="divide-y" data-testid="dia-kb-list">
            {records.map(r => (
              <div key={r.id} className="p-4 space-y-2" data-testid={`dia-kb-row-${r.slug}`}>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-semibold">{r.brand}</span>
                  <span className="text-muted-foreground">/</span>
                  <span>{r.model}</span>
                  <span className={`px-2 py-0.5 text-xs rounded ${STATUS_BADGE[r.status] || 'bg-slate-100'}`} data-testid={`dia-kb-status-${r.slug}`}>
                    {r.status}
                  </span>
                  <span className="px-2 py-0.5 text-xs rounded bg-slate-100">{r.category}</span>
                  {r.driver_classification && (
                    <span className="px-2 py-0.5 text-xs rounded bg-indigo-100 text-indigo-800">
                      {r.driver_classification}
                    </span>
                  )}
                </div>
                <div className="text-xs text-muted-foreground">
                  slug=<code>{r.slug}</code>
                  {r.aliases?.length > 0 && <> · aliases: {r.aliases.join(', ')}</>}
                  {r.evidence_sources?.length > 0 && <> · evidence: {r.evidence_sources.length}</>}
                </div>
                <div className="flex gap-2 flex-wrap">
                  {r.status === 'proposed' && (
                    <Button
                      size="sm" variant="outline"
                      onClick={() => doTransition(r.id, 'pending_review')}
                      disabled={transitionBusy === `${r.id}:pending_review`}
                      data-testid={`dia-transition-${r.slug}-pending_review`}
                    >
                      → pending_review
                    </Button>
                  )}
                  {r.status === 'pending_review' && (
                    <>
                      <Button
                        size="sm" variant="outline"
                        onClick={() => doTransition(r.id, 'approved_read_only')}
                        disabled={transitionBusy === `${r.id}:approved_read_only`}
                        data-testid={`dia-transition-${r.slug}-approved_read_only`}
                      >
                        → approved_read_only
                      </Button>
                      <Button
                        size="sm" variant="outline"
                        onClick={() => doTransition(r.id, 'approved_limited_control')}
                        disabled={transitionBusy === `${r.id}:approved_limited_control`}
                        data-testid={`dia-transition-${r.slug}-approved_limited_control`}
                      >
                        → approved_limited_control
                      </Button>
                      <Button
                        size="sm" variant="outline"
                        onClick={() => doTransition(r.id, 'approved_control')}
                        disabled={transitionBusy === `${r.id}:approved_control`}
                        title="Always blocked by DIA_CONTROL_CLAIMS_LOCK in P0/P1"
                        data-testid={`dia-transition-${r.slug}-approved_control`}
                      >
                        → approved_control (locked)
                      </Button>
                    </>
                  )}
                  <Button
                    size="sm" variant="ghost"
                    onClick={() => doTransition(r.id, 'needs_more_info')}
                    data-testid={`dia-transition-${r.slug}-needs_more_info`}
                  >
                    needs_more_info
                  </Button>
                  <Button
                    size="sm" variant="ghost"
                    onClick={() => doTransition(r.id, 'unsupported')}
                    data-testid={`dia-transition-${r.slug}-unsupported`}
                  >
                    unsupported
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


function PlaceholderTab({ label }) {
  return (
    <div className="rounded-lg border bg-card p-8 text-center" data-testid="dia-placeholder-tab">
      <Cpu className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
      <h3 className="font-semibold text-lg">{label}</h3>
      <p className="text-sm text-muted-foreground mt-2">
        Coming in DIA Phase 2/3/4. Backend collection is reserved; no writes allowed
        in P0/P1.
      </p>
    </div>
  );
}


export default function DeviceIntelligenceTab({ token }) {
  const [active, setActive] = useState('recognition');
  const [status, setStatus] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API}/api/admin/dia/status`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (!cancelled && d) setStatus(d); })
      .catch(() => { /* ignore */ });
    return () => { cancelled = true; };
  }, [token]);

  return (
    <div className="space-y-6" data-testid="dia-tab-root">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-xl font-semibold">Device Intelligence</h2>
          <p className="text-sm text-muted-foreground">
            Admin-only foundation. No customer-facing claims about device control.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {status?.control_claims_lock && (
            <span className="flex items-center gap-1 text-xs bg-rose-50 text-rose-800 px-2 py-1 rounded border border-rose-200">
              <Lock className="w-3 h-3" /> control_claims_lock=true
            </span>
          )}
          {status?.feature_enabled && (
            <span className="flex items-center gap-1 text-xs bg-emerald-50 text-emerald-800 px-2 py-1 rounded border border-emerald-200">
              <CheckCircle2 className="w-3 h-3" /> feature_enabled
            </span>
          )}
        </div>
      </div>

      <StatusBanner status={status} />

      <div className="flex flex-wrap gap-2 border-b">
        {SUB_TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setActive(t.id)}
            className={`px-3 py-2 text-sm border-b-2 -mb-px transition ${
              active === t.id ? 'border-primary text-primary font-semibold' : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
            data-testid={`dia-subtab-${t.id}`}
          >
            {t.label}
            {t.placeholder && <span className="ml-1 text-xs">(soon)</span>}
          </button>
        ))}
      </div>

      <div>
        {active === 'recognition' && <RecognitionEventsTab token={token} />}
        {active === 'knowledge' && <KnowledgeBaseTab token={token} />}
        {active === 'research_queue' && <PlaceholderTab label="Research Queue" />}
        {active === 'data_requests' && <PlaceholderTab label="Data Request Queue" />}
        {active === 'drivers' && <PlaceholderTab label="Driver Registry" />}
      </div>
    </div>
  );
}
